import React from "react";
import { Calendar, FileText, CloudSun, DollarSign, BarChart3, Sparkles } from "lucide-react";
import { GlowingEffect } from "../ui/glowing-effect";
import { LampLight } from "../ui/lamp";

const CARDS = [
  {
    area: "md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]",
    icon: Calendar,
    en: { title: "Bookings around the clock", desc: "Direct site, marketplace, kiosk and walk-ins — every channel lands in one manifest." },
    ar: { title: "حجوزات على مدار الساعة", desc: "الموقع المباشر، والسوق، والكشك، والحجوزات الفورية — كل القنوات في سجل واحد." },
  },
  {
    area: "md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]",
    icon: FileText,
    en: { title: "Waivers signed before the dock", desc: "OTP-verified e-sign with automatic reminders — no clipboard, no chasing." },
    ar: { title: "تنازلات موقّعة قبل الرصيف", desc: "توقيع إلكتروني موثّق برمز مع تذكيرات تلقائية — بلا أوراق وبلا ملاحقة." },
  },
  {
    area: "md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]",
    icon: DollarSign,
    en: { title: "Paid instantly, your way", desc: "Stripe or Ziina. Deposits, balances, tips and extras — settled straight to your account." },
    ar: { title: "استلم أموالك فوراً، بطريقتك", desc: "Stripe أو Ziina. عربون، أرصدة، إكراميات وإضافات — تصل مباشرة إلى حسابك." },
  },
  {
    area: "md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]",
    icon: CloudSun,
    en: { title: "Weather-aware days", desc: "Marine forecasts pulled into every manifest so you make the call early, not at the dock." },
    ar: { title: "أيام واعية بالطقس", desc: "توقعات بحرية داخل كل سجل لتتخذ القرار مبكراً، لا عند الرصيف." },
  },
  {
    area: "md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]",
    icon: BarChart3,
    en: { title: "HELM Intelligence", desc: "Asset ranking, P&L and campaign reporting that reads like a chartroom briefing." },
    ar: { title: "ذكاء HELM", desc: "ترتيب الأصول، الأرباح والخسائر، وتقارير الحملات — بوضوح غرفة الخرائط." },
  },
];

const GlowCard = ({ area, icon: Icon, title, desc, index }) => (
  <li className={`min-h-[13rem] list-none ${area}`} data-testid={`glow-card-${index}`}>
    <div className="relative h-full rounded-3xl border border-gray-100 p-2 md:p-3">
      <GlowingEffect spread={40} glow={true} disabled={false} proximity={64} inactiveZone={0.01} borderWidth={2} />
      <div className="relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-2xl bg-gray-50 p-6">
        <div className="w-fit rounded-xl bg-blue-600/10 p-2.5">
          <Icon className="h-4 w-4 text-blue-600" strokeWidth={2} />
        </div>
        <div className="space-y-2">
          <h3 className="font-sans text-xl md:text-2xl font-semibold tracking-tight text-gray-900">{title}</h3>
          <p className="font-sans text-sm md:text-base text-gray-600 leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  </li>
);

export const GlowingCardsSection = ({ isAr }) => (
  <section className="pb-24 bg-white" data-testid="supplier-glowing-cards-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Lamp header — the blue beam lights the software grid */}
      <LampLight>
        <div className="text-center" dir={isAr ? "rtl" : undefined}>
          <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {isAr ? "كل شيء في مكان واحد" : "Everything in one deck"}
          </div>
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1]">
            {isAr ? (
              <>أدر العملية كاملة <span className="text-blue-600">من مكان واحد.</span></>
            ) : (
              <>Run the whole operation <span className="text-blue-600">from one place.</span></>
            )}
          </h3>
        </div>
      </LampLight>
      <ul className="grid grid-cols-1 gap-4 md:grid-cols-12 md:grid-rows-3 xl:grid-rows-2 lg:gap-5 -mt-6" dir={isAr ? "rtl" : undefined}>
        {CARDS.map((c, i) => (
          <GlowCard key={i} index={i} area={c.area} icon={c.icon} title={isAr ? c.ar.title : c.en.title} desc={isAr ? c.ar.desc : c.en.desc} />
        ))}
      </ul>
    </div>
  </section>
);
